'use client'

import Link from 'next/link'
import Image from 'next/image'
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
} from 'lucide-react'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { LayoutGroup, motion } from 'motion/react'

import { containerClassName } from '@/components/Container'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  groupApartmentsByLocation,
  groupApartmentsByRoomType,
  type LocationDropdownOption,
  type RoomTypeListingItem,
} from '@/lib/apartments/apartmentsQuery'
import {
  communitySpacesForLocation,
  type AvailabilitySectionItem,
} from '@/lib/apartments/availabilitySectionData'
import type { LocationCommunitySpaces } from '@/lib/locations/locationCommunitySpaces'

type SectionRoomTypeGroup = RoomTypeListingItem<AvailabilitySectionItem>
import { getApartmentHref } from '@/lib/apartments/apartmentPaths'
import { resolveApartmentRentDisplayCopy } from '@/lib/apartments/apartmentRentDisplayCopy'
import { resolveApartmentRentLink } from '@/lib/apartments/apartmentBookingLink'
import { getInspectionCalendarHref } from '@/lib/pages/inspectionCalendarLink'
import { getLocationHref } from '@/lib/mock/locationPages'
import { formatPriceNumber } from '@/lib/pricing/formatPriceNumber'
import {
  getCommunitySpaceSlides,
  hasCommunitySpaceContent,
  isCommunitySpaceImageCarouselTab,
  type CommunitySpaceTab,
} from '@/lib/locations/locationCommunitySpaces'
import { RoomGalleryVideoPanel } from '@/components/Room/Gallery/RoomGalleryVideoPanel'
import { localizePath } from '@/lib/i18n/config'
import { useMessages } from '@/lib/i18n/useMessages'
import { useLocale } from '@/lib/i18n/useLocale'
import { cn } from '@/utilities/ui'

type ViewTab = 'apartments' | 'community'

function ApartmentsTabIcon() {
  return (
    <svg
      aria-hidden
      className="size-5 shrink-0"
      fill="none"
      height={24}
      viewBox="0 0 24 24"
      width={24}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 20V12C2 11.4696 2.21071 10.9609 2.58579 10.5858C2.96086 10.2107 3.46957 10 4 10H20C20.5304 10 21.0391 10.2107 21.4142 10.5858C21.7893 10.9609 22 11.4696 22 12V20"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M4 10V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M12 4V10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M2 18H22"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function CommunitySpacesTabIcon() {
  return (
    <svg
      aria-hidden
      className="size-5 shrink-0"
      fill="none"
      height={24}
      viewBox="0 0 24 24"
      width={24}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M9 9H9.01"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        d="M15 9H15.01"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

const GLASS_SURFACE_CLASS =
  'border-white/10 bg-[rgba(0,33,64,0.80)] shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)] backdrop-blur-xl md:shadow-[0px_8px_24px_0px_rgba(0,20,46,0.22)]'

const TAB_BAR_SHELL_CLASS =
  'relative rounded-[10px] border border-white/10 bg-[rgba(0,33,64,0.80)] p-1 shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)] backdrop-blur-xl md:rounded-xl'

const VARIANT_TAB_BAR_SHELL_CLASS =
  'relative rounded-[10px] border border-white/10 bg-transparent p-1 md:rounded-xl'

const TAB_BAR_HEIGHT_CLASS = 'h-12 md:h-[4.25rem]'

const TAB_INDICATOR_SURFACE_CLASS =
  'pointer-events-none absolute inset-0 rounded-[10px] bg-[#8DC8FF] shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)]'

const TAB_SPRING_TRANSITION = { type: 'spring' as const, stiffness: 400, damping: 35 }

const APARTMENT_SELECT_TRIGGER_CLASS =
  'relative z-10 flex !h-14 !w-full items-center gap-2 rounded-[10px] border border-white/10 bg-[linear-gradient(110deg,rgba(4,43,73,0.96),rgba(18,62,91,0.94))] px-3 py-1 font-outfit shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)] backdrop-blur-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:border-turquoise/70 md:gap-3 md:rounded-xl md:px-4 md:py-3 md:shadow-[0px_8px_24px_0px_rgba(0,20,46,0.22)] md:!h-[4.25rem] [&>svg:last-child]:size-5 [&>svg:last-child]:text-white/80 [&>svg:last-child]:transition-transform [&>svg:last-child]:duration-200'

const ROOM_TYPE_ARROW_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-full border border-transparent bg-[#8DC8FF] text-dark-800 transition-colors hover:bg-[#7ab8ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 disabled:pointer-events-none disabled:opacity-40'

const CAROUSEL_ARROW_BUTTON_CLASS =
  'static shrink-0 translate-none rounded-full border-[1.5px] border-white/95 bg-black/70 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

const APARTMENT_SELECT_CONTENT_CLASS =
  'z-[60] w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] max-h-[min(30rem,calc(100vh-8rem))] overflow-x-hidden overflow-y-auto overscroll-contain rounded-none border border-white/10 bg-[rgba(0,33,64,0.95)] p-0 text-white shadow-[0px_12px_32px_0px_rgba(0,20,46,0.35)] ring-0 data-[side=bottom]:-mt-px data-[side=bottom]:translate-y-0 data-[side=bottom]:border-t-0 data-[side=bottom]:rounded-b-[10px] md:data-[side=bottom]:rounded-b-xl data-[side=top]:-mb-px data-[side=top]:translate-y-0 data-[side=top]:border-b-0 data-[side=top]:rounded-t-[10px] md:data-[side=top]:rounded-t-xl'

const APARTMENT_SELECT_ITEM_CLASS =
  'group rounded-none px-4 py-3 text-left font-outfit text-white focus:bg-white/5 focus:!text-white focus:**:!text-white data-highlighted:bg-white/5 data-highlighted:!text-white data-highlighted:**:!text-white data-[state=checked]:bg-[#00528f] data-[state=checked]:!text-white data-[state=checked]:**:!text-white [&>span:first-child]:hidden'

const APARTMENT_VARIANT_TAB_INDICATOR_ID = 'apartment-availability-variant-tab-indicator'

const GLASS_PANEL_CLASS = GLASS_SURFACE_CLASS

const GLASS_ACTION_BUTTON_CLASS =
  'relative h-full min-h-12 flex-1 overflow-hidden rounded-[10px] !border !border-white/10 px-4 font-outfit text-sm font-medium tracking-[0.02em] !text-white/95 backdrop-blur-xl transition-[background,box-shadow] md:h-[3.75rem] md:rounded-xl md:text-base !bg-[rgba(0,33,64,0.80)] shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12),inset_0_-1px_0_rgba(255,255,255,0.14)] hover:!bg-[rgba(0,33,64,0.88)] md:shadow-[0px_8px_24px_0px_rgba(0,20,46,0.22),inset_0_-1px_0_rgba(255,255,255,0.14)]'

const PRIMARY_ACTION_BUTTON_CLASS =
  'h-full min-h-12 flex-1 rounded-[10px] px-4 font-outfit text-sm font-medium tracking-[0.02em] text-dark-800 md:h-[3.75rem] md:rounded-xl md:px-8 md:py-5 md:text-base'

function getVariantTabLines(apartment: AvailabilitySectionItem): {
  sizeLabel: string
  title: string
} {
  const sizeSuffixPattern = new RegExp(`\\s*[-/]?\\s*${apartment.size}\\s*m²\\s*$`, 'i')
  const title = sizeSuffixPattern.test(apartment.name)
    ? apartment.name.replace(sizeSuffixPattern, '').trim()
    : apartment.name

  return {
    title,
    sizeLabel: `${apartment.size}m²`,
  }
}

function getPrimaryVariantForLocation(
  locationId: number,
  roomTypeGroups: SectionRoomTypeGroup[],
): SectionRoomTypeGroup | undefined {
  return roomTypeGroups.find((group) => group.locationId === locationId)
}

type ApartmentVariantTabsProps = {
  'aria-label': string
  layoutGroupId: string
  onSelect: (apartmentId: string) => void
  selectedApartmentId: string
  variants: AvailabilitySectionItem[]
}

function ApartmentVariantTabs({
  'aria-label': ariaLabel,
  layoutGroupId,
  onSelect,
  selectedApartmentId,
  variants,
}: ApartmentVariantTabsProps) {
  if (variants.length <= 1) {
    return null
  }

  return (
    <LayoutGroup id={layoutGroupId}>
      <div
        aria-label={ariaLabel}
        className={cn(VARIANT_TAB_BAR_SHELL_CLASS, 'h-auto min-h-12 md:min-h-[3.25rem]')}
        role="tablist"
      >
        <div className="relative z-10 flex gap-1">
          {variants.map((variant) => {
            const isActive = variant.id === selectedApartmentId
            const { sizeLabel, title } = getVariantTabLines(variant)
            const tabLabel = `${title} ${sizeLabel}`

            return (
              <button
                key={variant.id}
                aria-label={tabLabel}
                aria-selected={isActive}
                className={cn(segmentedTabButtonClassName(), 'min-h-12 flex-1 md:min-h-[3.25rem]')}
                onClick={() => onSelect(variant.id)}
                role="tab"
                type="button"
              >
                {isActive ? (
                  <motion.div
                    aria-hidden
                    className={TAB_INDICATOR_SURFACE_CLASS}
                    layoutId={APARTMENT_VARIANT_TAB_INDICATOR_ID}
                    transition={TAB_SPRING_TRANSITION}
                  />
                ) : null}
                <span className={segmentedTabContentClassName(isActive)}>
                  <span className="flex min-w-0 flex-col items-center gap-0.5">
                    <span className="max-w-full truncate text-sm font-semibold leading-tight md:text-base">
                      {title}
                    </span>
                    <span
                      className={cn(
                        'text-xs leading-tight md:text-sm',
                        isActive ? 'text-dark-800' : 'text-white/65',
                      )}
                    >
                      {sizeLabel}
                    </span>
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </LayoutGroup>
  )
}

type RoomTypeDetailPanelProps = {
  copy: {
    apartmentTypeLabel: string
    nextRoomType: string
    previousRoomType: string
    rentEyebrowAfter: string
    rentEyebrowBefore: string
    rentEyebrowHighlight: string
    rentTermLabel: string
    roomTypePosition: string
    startingAtLabel: string
    variantTabsAriaLabel: string
  }
  formatPrice: (price: number) => string
  layoutGroupId: string
  onNextRoomType: () => void
  onPreviousRoomType: () => void
  onVariantSelect: (apartmentId: string) => void
  roomTypeCount: number
  roomTypeIndex: number
  selectedApartment: AvailabilitySectionItem
  selectedRoomTypeGroup: SectionRoomTypeGroup
}

function RoomTypeDetailPanel({
  copy,
  formatPrice,
  layoutGroupId,
  onNextRoomType,
  onPreviousRoomType,
  onVariantSelect,
  roomTypeCount,
  roomTypeIndex,
  selectedApartment,
  selectedRoomTypeGroup,
}: RoomTypeDetailPanelProps) {
  const canNavigateRoomTypes = roomTypeCount > 1
  const positionLabel = copy.roomTypePosition
    .replace('{current}', String(roomTypeIndex + 1))
    .replace('{total}', String(roomTypeCount))
  const rentCopy = resolveApartmentRentDisplayCopy(selectedApartment, {
    rentPrimaryText: `${copy.rentEyebrowBefore}${copy.rentEyebrowHighlight}${copy.rentEyebrowAfter}`,
    rentSecondaryText: copy.rentTermLabel,
  })

  return (
    <div
      className={cn(
        'relative z-10 rounded-[10px] border border-white/10 px-3 py-3 font-outfit md:rounded-xl md:px-5 md:py-5',
        GLASS_PANEL_CLASS,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-white/75">{copy.apartmentTypeLabel}</p>
        {canNavigateRoomTypes ? (
          <div className="flex items-center gap-1">
            <button
              aria-label={copy.previousRoomType}
              className={cn(ROOM_TYPE_ARROW_BUTTON_CLASS, 'size-8')}
              disabled={roomTypeIndex === 0}
              onClick={onPreviousRoomType}
              type="button"
            >
              <ChevronLeft aria-hidden className="size-4" />
            </button>
            <span className="min-w-[4.5rem] text-center text-sm font-medium text-white/85">
              {positionLabel}
            </span>
            <button
              aria-label={copy.nextRoomType}
              className={cn(ROOM_TYPE_ARROW_BUTTON_CLASS, 'size-8')}
              disabled={roomTypeIndex >= roomTypeCount - 1}
              onClick={onNextRoomType}
              type="button"
            >
              <ChevronRight aria-hidden className="size-4" />
            </button>
          </div>
        ) : null}
      </div>

      <h3 className="mt-2 font-display text-[1.625rem] font-medium leading-tight tracking-[-0.02em] text-white md:mt-3 md:text-[2rem] md:leading-none">
        {selectedApartment.name}
      </h3>

      {selectedApartment.description ? (
        <p className="mt-2 text-sm leading-5 text-white/75 md:mt-3 md:text-base md:leading-6">
          {selectedApartment.description}
        </p>
      ) : null}

      <div className="mt-3 flex flex-row items-end justify-between gap-3 md:mt-4">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-5 text-white underline decoration-wavy decoration-turquoise underline-offset-4 md:text-base">
            {rentCopy.rentPrimaryText}
          </p>
          <p className="mt-1 text-sm leading-5 text-white/65 md:text-base">{rentCopy.rentSecondaryText}</p>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-xs leading-4 md:text-sm md:leading-5 text-turquoise">
            {copy.startingAtLabel}
          </p>
          <p className="font-outfit text-xl font-medium leading-6 text-turquoise md:text-2xl md:leading-7">
            {formatPrice(selectedApartment.price)} €
          </p>
        </div>
      </div>

      <div className="mt-3 md:mt-4">
        <ApartmentVariantTabs
          aria-label={copy.variantTabsAriaLabel}
          layoutGroupId={layoutGroupId}
          onSelect={onVariantSelect}
          selectedApartmentId={selectedApartment.id}
          variants={selectedRoomTypeGroup.variants}
        />
      </div>
    </div>
  )
}

function useSelectMenuSide(open: boolean) {
  const [menuSide, setMenuSide] = useState<'top' | 'bottom'>('bottom')
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open) {
      setMenuSide('bottom')
      return
    }

    const syncMenuSide = () => {
      const side = contentRef.current?.getAttribute('data-side')
      if (side === 'top' || side === 'bottom') {
        setMenuSide(side)
      }
    }

    syncMenuSide()
    const frame = requestAnimationFrame(syncMenuSide)
    return () => cancelAnimationFrame(frame)
  }, [open])

  return { contentRef, menuSide }
}

type SegmentedTabBarProps = {
  'aria-label'?: string
  children: React.ReactNode
  columnCount: 1 | 2 | 3
  layoutGroupId: string
  role?: string
}

function SegmentedTabBar({
  'aria-label': ariaLabel,
  children,
  columnCount,
  layoutGroupId,
  role,
}: SegmentedTabBarProps) {
  return (
    <LayoutGroup id={layoutGroupId}>
      <div
        aria-label={ariaLabel}
        className={cn(TAB_BAR_SHELL_CLASS, TAB_BAR_HEIGHT_CLASS)}
        role={role}
      >
        <div
          className={cn(
            'relative z-10 grid h-full gap-1',
            columnCount === 1
              ? 'grid-cols-1'
              : columnCount === 2
                ? 'grid-cols-2'
                : 'grid-cols-3',
          )}
        >
          {children}
        </div>
      </div>
    </LayoutGroup>
  )
}

const segmentedTabButtonClassName = () =>
  'group relative z-10 inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-[10px] px-3 py-1.5 font-outfit text-sm font-medium whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-inset'

const segmentedTabContentClassName = (isActive: boolean) =>
  cn(
    'relative z-10 inline-flex w-full items-center justify-center gap-2 transition-colors duration-300 ease-out motion-reduce:transition-none',
    isActive ? 'text-dark-800 delay-100' : 'text-white/80 delay-0 group-hover:text-white',
  )

type SegmentedTabProps = {
  children: React.ReactNode
  indicatorLayoutId: string
  isActive: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

function SegmentedTab({
  children,
  className,
  indicatorLayoutId,
  isActive,
  ...props
}: SegmentedTabProps) {
  return (
    <button
      className={cn(segmentedTabButtonClassName(), className)}
      type="button"
      {...props}
    >
      {isActive ? (
        <motion.div
          aria-hidden
          className={TAB_INDICATOR_SURFACE_CLASS}
          layoutId={indicatorLayoutId}
          transition={TAB_SPRING_TRANSITION}
        />
      ) : null}
      <span className={segmentedTabContentClassName(isActive)}>{children}</span>
    </button>
  )
}

type ApartmentDropdownProps = {
  apartments: AvailabilitySectionItem[]
  displayMode?: 'apartment' | 'location' | 'roomType' | 'variant'
  getPriceLabel: (price: number) => string
  locationOptions?: LocationDropdownOption[]
  onSelect: (apartment: AvailabilitySectionItem) => void
  roomTypeOptions?: SectionRoomTypeGroup[]
  selectedApartment: AvailabilitySectionItem
  selectLabel: string
}

function ApartmentDropdown({
  apartments,
  displayMode = 'apartment',
  getPriceLabel,
  locationOptions = [],
  onSelect,
  roomTypeOptions = [],
  selectedApartment,
  selectLabel,
}: ApartmentDropdownProps) {
  const [open, setOpen] = useState(false)
  const { contentRef, menuSide } = useSelectMenuSide(open)
  const isLocationMode = displayMode === 'location'
  const isRoomTypeMode = displayMode === 'roomType'
  const primaryLabel = isLocationMode
    ? selectedApartment.location
    : isRoomTypeMode
      ? selectedApartment.roomTypeLabel
      : selectedApartment.name
  const selectValue = isLocationMode
    ? String(selectedApartment.locationId)
    : isRoomTypeMode
      ? `${selectedApartment.locationId}:${selectedApartment.roomType}`
      : selectedApartment.id
  const triggerPrice = isLocationMode
    ? (locationOptions.find((option) => option.locationId === selectedApartment.locationId)
        ?.price ?? selectedApartment.price)
    : selectedApartment.price

  const handleLocationSelect = (locationId: string) => {
    const option = locationOptions.find((entry) => String(entry.locationId) === locationId)
    if (!option) return

    const apartment = apartments.find((entry) => entry.id === option.apartmentId)
    if (apartment) onSelect(apartment)
  }

  const handleRoomTypeSelect = (groupKey: string) => {
    const listing = roomTypeOptions.find(
      (entry) => `${entry.locationId}:${entry.roomType}` === groupKey,
    )
    if (listing) onSelect(listing)
  }

  return (
    <div className="w-full min-w-0">
      <Select
        onOpenChange={setOpen}
        onValueChange={(value) => {
          if (isLocationMode) {
            handleLocationSelect(value)
            return
          }

          if (isRoomTypeMode) {
            handleRoomTypeSelect(value)
            return
          }

          const apartment = apartments.find((option) => option.id === value)
          if (apartment) onSelect(apartment)
        }}
        open={open}
        value={selectValue}
      >
      <SelectTrigger
        aria-label={`${selectLabel}: ${primaryLabel}`}
        className={cn(
          APARTMENT_SELECT_TRIGGER_CLASS,
          open &&
            menuSide === 'bottom' &&
            'rounded-b-none border-b-transparent rounded-t-[10px] md:rounded-t-xl md:rounded-b-none',
          open &&
            menuSide === 'top' &&
            'rounded-t-none border-t-transparent rounded-b-[10px] md:rounded-b-xl md:rounded-t-none',
          open && '[&>svg:last-child]:rotate-180',
        )}
      >
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#003c73] text-white/90 shadow-inner shadow-black/10">
          <MapPin aria-hidden className="size-[1.125rem]" />
        </span>
        <SelectValue>
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-base font-semibold leading-5 tracking-[0.01em] text-white">
              {primaryLabel}
            </span>
            <span className="mt-1 block truncate text-sm font-medium leading-4 text-white/75">
              {getPriceLabel(triggerPrice)}
            </span>
          </span>
        </SelectValue>
      </SelectTrigger>

      <SelectContent
        ref={contentRef}
        align="start"
        avoidCollisions
        className={APARTMENT_SELECT_CONTENT_CLASS}
        collisionPadding={{ top: 16, bottom: 16, left: 8, right: 8 }}
        data-lenis-prevent
        hideScrollButtons
        onTouchMove={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
        position="popper"
        sideOffset={0}
        viewportClassName="block !h-auto !min-h-0 w-full min-w-[var(--radix-select-trigger-width)] p-0 data-[position=popper]:!h-auto data-[position=popper]:w-full"
      >
        {isLocationMode
          ? locationOptions.map((option, index) => (
              <React.Fragment key={option.locationId}>
                <SelectItem
                  className={APARTMENT_SELECT_ITEM_CLASS}
                  textValue={option.location}
                  value={String(option.locationId)}
                >
                  <span className="flex w-full flex-col gap-px">
                    <span className="font-outfit text-sm font-semibold text-white group-focus:text-white group-data-highlighted:text-white group-data-[state=checked]:text-white">
                      {option.location}
                    </span>
                    <span className="font-outfit text-[11px] text-white/60 group-focus:text-white/80 group-data-highlighted:text-white/80 group-data-[state=checked]:text-white">
                      {getPriceLabel(option.price)}
                    </span>
                  </span>
                </SelectItem>
                {index < locationOptions.length - 1 ? (
                  <SelectSeparator className="my-0 h-px bg-white/[0.08]" />
                ) : null}
              </React.Fragment>
            ))
          : isRoomTypeMode
            ? roomTypeOptions.map((listing, index) => (
                <React.Fragment key={`${listing.locationId}-${listing.roomType}`}>
                  <SelectItem
                    className={APARTMENT_SELECT_ITEM_CLASS}
                    textValue={listing.roomTypeLabel}
                    value={`${listing.locationId}:${listing.roomType}`}
                  >
                    <span className="flex w-full flex-col gap-px">
                      <span className="font-outfit text-sm font-semibold text-white group-focus:text-white group-data-highlighted:text-white group-data-[state=checked]:text-white">
                        {listing.roomTypeLabel}
                      </span>
                      <span className="font-outfit text-[11px] text-white/60 group-focus:text-white/80 group-data-highlighted:text-white/80 group-data-[state=checked]:text-white">
                        {getPriceLabel(listing.startingPrice)}
                      </span>
                    </span>
                  </SelectItem>
                  {index < roomTypeOptions.length - 1 ? (
                    <SelectSeparator className="my-0 h-px bg-white/[0.08]" />
                  ) : null}
                </React.Fragment>
              ))
            : apartments.map((apartment, index) => (
              <React.Fragment key={apartment.id}>
                <SelectItem
                  className={APARTMENT_SELECT_ITEM_CLASS}
                  textValue={apartment.name}
                  value={apartment.id}
                >
                  <span className="flex w-full flex-col gap-px">
                    <span className="font-outfit text-sm font-semibold text-white group-focus:text-white group-data-highlighted:text-white group-data-[state=checked]:text-white">
                      {apartment.name}
                    </span>
                    <span className="font-outfit text-[11px] text-white/60 group-focus:text-white/80 group-data-highlighted:text-white/80 group-data-[state=checked]:text-white">
                      {apartment.location}
                    </span>
                  </span>
                </SelectItem>
                {index < apartments.length - 1 ? (
                  <SelectSeparator className="my-0 h-px bg-white/[0.08]" />
                ) : null}
              </React.Fragment>
            ))}
      </SelectContent>
    </Select>
    </div>
  )
}

type CommunitySpacesPanelProps = {
  activeCommunityTab: CommunitySpaceTab
  bookVisit: string
  bookVisitHref: string
  communityTabs: {
    floorPlans: string
    gallery: string
    virtualTour: string
  }
  communityTabListAriaLabel: string
  enabledTabs: CommunitySpaceTab[]
  onCommunityTabChange: (tab: CommunitySpaceTab) => void
  rentApartments: string
  rentApartmentsHref: string
  showLocationDropdown?: boolean
  apartments?: AvailabilitySectionItem[]
  getPriceLabel?: (price: number) => string
  locationOptions?: LocationDropdownOption[]
  onLocationSelect?: (apartment: AvailabilitySectionItem) => void
  selectedApartment?: AvailabilitySectionItem
  locationSelectLabel?: string
}

function CommunitySpacesPanel({
  activeCommunityTab,
  bookVisit,
  bookVisitHref,
  communityTabs,
  communityTabListAriaLabel,
  enabledTabs,
  onCommunityTabChange,
  rentApartments,
  rentApartmentsHref,
  showLocationDropdown = false,
  apartments = [],
  getPriceLabel,
  locationOptions = [],
  onLocationSelect,
  selectedApartment,
  locationSelectLabel,
}: CommunitySpacesPanelProps) {
  const messages = useMessages()

  return (
    <>
      {showLocationDropdown && selectedApartment && onLocationSelect && getPriceLabel ? (
        <ApartmentDropdown
          apartments={apartments}
          displayMode="location"
          getPriceLabel={getPriceLabel}
          locationOptions={locationOptions}
          onSelect={onLocationSelect}
          selectedApartment={selectedApartment}
          selectLabel={locationSelectLabel ?? ''}
        />
      ) : null}

      <SegmentedTabBar
        aria-label={communityTabListAriaLabel}
        columnCount={
          enabledTabs.length >= 3 ? 3 : enabledTabs.length === 2 ? 2 : 1
        }
        layoutGroupId="community-space-tabs"
        role="tablist"
      >
        {enabledTabs.map((tab) => (
          <SegmentedTab
            key={tab}
            aria-selected={activeCommunityTab === tab}
            indicatorLayoutId="community-space-tab-indicator"
            isActive={activeCommunityTab === tab}
            onClick={() => onCommunityTabChange(tab)}
            role="tab"
          >
            {communityTabs[tab]}
          </SegmentedTab>
        ))}
      </SegmentedTabBar>

      <div className="flex w-full gap-1.5 pt-1 md:gap-2 md:pt-2">
        <Button
          asChild
          className={GLASS_ACTION_BUTTON_CLASS}
          variant="outline"
        >
          <Link href={bookVisitHref} rel="noopener noreferrer" target="_blank">
            {bookVisit}
            <span className="sr-only"> {messages.common.opensInNewTab}</span>
          </Link>
        </Button>
        <Button
          asChild
          className={PRIMARY_ACTION_BUTTON_CLASS}
          icon={
            <img
              alt=""
              aria-hidden
              className="size-5 shrink-0"
              height={20}
              loading="lazy"
              src="/media/homepage/rent-arrow.svg"
              width={20}
            />
          }
          iconPosition="trailing"
        >
          <Link href={rentApartmentsHref}>{rentApartments}</Link>
        </Button>
      </div>
    </>
  )
}

type ApartmentAvailabilitySectionProps = {
  apartments?: AvailabilitySectionItem[]
  communitySpacesByLocation?: Record<string, LocationCommunitySpaces>
  variant?: 'landing' | 'location'
}

export const ApartmentAvailabilitySection: React.FC<ApartmentAvailabilitySectionProps> = ({
  apartments = [],
  communitySpacesByLocation = {},
  variant = 'location',
}) => {
  const messages = useMessages()
  const locale = useLocale()
  const copy = messages.landing.apartmentAvailability

  const allRoomTypeGroups = useMemo(() => groupApartmentsByRoomType(apartments), [apartments])

  const locationDropdownOptions = useMemo(
    () => groupApartmentsByLocation(apartments),
    [apartments],
  )

  const communityLocationOptions = useMemo(() => {
    const locationIdsWithSpaces = new Set(
      Object.entries(communitySpacesByLocation)
        .filter(([, spaces]) => hasCommunitySpaceContent(spaces))
        .map(([locationId]) => Number(locationId)),
    )

    return groupApartmentsByLocation(apartments).filter((option) =>
      locationIdsWithSpaces.has(option.locationId),
    )
  }, [apartments, communitySpacesByLocation])

  const hasAnyCommunitySpaces = useMemo(
    () => Object.values(communitySpacesByLocation).some((spaces) => hasCommunitySpaceContent(spaces)),
    [communitySpacesByLocation],
  )

  const initialApartmentId = useMemo(() => {
    if (variant === 'location') {
      return allRoomTypeGroups[0]?.id ?? apartments[0]?.id ?? null
    }

    const firstLocation = locationDropdownOptions[0]
    if (!firstLocation) {
      return apartments[0]?.id ?? null
    }

    const firstRoomTypeGroup = getPrimaryVariantForLocation(
      firstLocation.locationId,
      allRoomTypeGroups,
    )

    return firstRoomTypeGroup?.id ?? firstLocation.apartmentId
  }, [allRoomTypeGroups, apartments, locationDropdownOptions, variant])

  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(initialApartmentId)
  const [activeTab, setActiveTab] = useState<ViewTab>('apartments')
  const [activeCommunityTab, setActiveCommunityTab] = useState<CommunitySpaceTab>('gallery')
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()

  const selectedApartment =
    apartments.find((apartment) => apartment.id === selectedApartmentId) ?? apartments[0]

  const selectedCommunitySpaces = communitySpacesForLocation(
    communitySpacesByLocation,
    selectedApartment?.locationId,
  )
  const enabledCommunityTabs = selectedCommunitySpaces?.tabs ?? []
  const showCommunityTab =
    variant === 'landing'
      ? hasAnyCommunitySpaces
      : hasCommunitySpaceContent(selectedCommunitySpaces)

  const contextualRoomTypeGroups = useMemo(() => {
    if (!selectedApartment) return allRoomTypeGroups

    if (variant === 'landing') {
      return allRoomTypeGroups.filter(
        (group) => group.locationId === selectedApartment.locationId,
      )
    }

    return allRoomTypeGroups
  }, [allRoomTypeGroups, selectedApartment, variant])

  const selectedRoomTypeGroup =
    selectedApartment &&
    contextualRoomTypeGroups.find(
      (group) =>
        group.locationId === selectedApartment.locationId &&
        group.roomType === selectedApartment.roomType,
    )

  const selectedRoomTypeIndex = selectedRoomTypeGroup
    ? contextualRoomTypeGroups.findIndex((group) => group.id === selectedRoomTypeGroup.id)
    : -1

  const apartmentSlides = selectedApartment?.images ?? []

  const roomDetailsHref =
    selectedApartment != null
      ? selectedApartment.slug
        ? getApartmentHref(
            selectedApartment.locationCitySlug,
            selectedApartment.locationSlug,
            selectedApartment.slug,
            locale,
          )
        : getLocationHref(
            selectedApartment.locationCitySlug,
            selectedApartment.locationSlug,
            locale,
          )
      : copy.rentApartmentsHref

  const rentLink = resolveApartmentRentLink(
    locale,
    selectedApartment?.slug,
    selectedApartment?.externalBookingUrl,
  )

  const formatPrice = useCallback(
    (price: number) => formatPriceNumber(price, locale),
    [locale],
  )

  const getPriceLabel = useCallback(
    (price: number) => copy.priceLabel.replace('{price}', formatPrice(price)),
    [copy.priceLabel, formatPrice],
  )

  const handleLocationSelect = useCallback(
    (apartment: AvailabilitySectionItem) => {
      const firstRoomTypeGroup = getPrimaryVariantForLocation(
        apartment.locationId,
        allRoomTypeGroups,
      )

      setSelectedApartmentId(firstRoomTypeGroup?.id ?? apartment.id)
    },
    [allRoomTypeGroups],
  )

  const handlePreviousRoomType = useCallback(() => {
    if (selectedRoomTypeIndex <= 0) return

    const previousGroup = contextualRoomTypeGroups[selectedRoomTypeIndex - 1]
    if (previousGroup) {
      setSelectedApartmentId(previousGroup.id)
    }
  }, [contextualRoomTypeGroups, selectedRoomTypeIndex])

  const handleNextRoomType = useCallback(() => {
    if (
      selectedRoomTypeIndex < 0 ||
      selectedRoomTypeIndex >= contextualRoomTypeGroups.length - 1
    ) {
      return
    }

    const nextGroup = contextualRoomTypeGroups[selectedRoomTypeIndex + 1]
    if (nextGroup) {
      setSelectedApartmentId(nextGroup.id)
    }
  }, [contextualRoomTypeGroups, selectedRoomTypeIndex])

  const selectionAnnouncement =
    selectedApartment && selectedRoomTypeGroup
      ? copy.selectionAnnouncement
          .replace('{roomType}', selectedRoomTypeGroup.roomTypeLabel)
          .replace('{variant}', selectedApartment.name)
          .replace('{price}', formatPrice(selectedApartment.price))
      : ''

  useEffect(() => {
    if (activeTab !== 'community') return

    if (!showCommunityTab) {
      setActiveTab('apartments')
      return
    }

    if (
      enabledCommunityTabs.length > 0 &&
      !enabledCommunityTabs.includes(activeCommunityTab)
    ) {
      setActiveCommunityTab(enabledCommunityTabs[0]!)
    }
  }, [activeCommunityTab, activeTab, enabledCommunityTabs, showCommunityTab])

  useEffect(() => {
    if (activeTab !== 'community' || variant !== 'landing') return
    if (hasCommunitySpaceContent(selectedCommunitySpaces)) return
    if (communityLocationOptions.length === 0) return

    const firstOption = communityLocationOptions[0]
    const firstApartment = apartments.find(
      (apartment) => apartment.locationId === firstOption?.locationId,
    )

    if (firstApartment) {
      handleLocationSelect(firstApartment)
    }
  }, [
    activeTab,
    apartments,
    communityLocationOptions,
    handleLocationSelect,
    selectedCommunitySpaces,
    variant,
  ])

  useEffect(() => {
    setSelectedApartmentId((current) => {
      if (current && apartments.some((apartment) => apartment.id === current)) {
        return current
      }

      return initialApartmentId
    })
  }, [apartments, initialApartmentId])

  useEffect(() => {
    carouselApi?.scrollTo(0, true)
  }, [carouselApi, selectedApartment?.id, selectedApartment?.locationId, variant, activeTab, activeCommunityTab])

  if (apartments.length === 0 || !selectedApartment) {
    return (
      <section aria-labelledby="apartment-availability-heading" className="bg-bluetext py-16">
        <div className={cn(containerClassName, 'py-0')}>
          <h2
            className="font-display text-[2rem] font-semibold text-white md:text-[2.5rem]"
            id="apartment-availability-heading"
          >
            {copy.title}
          </h2>
          <p className="mt-4 font-outfit text-base text-white/80">{copy.emptyState}</p>
        </div>
      </section>
    )
  }

  const communitySlides =
    selectedCommunitySpaces && isCommunitySpaceImageCarouselTab(activeCommunityTab)
      ? getCommunitySpaceSlides(selectedCommunitySpaces, activeCommunityTab)
      : []

  const slides = activeTab === 'apartments' ? apartmentSlides : communitySlides
  const showCommunityImageCarousel =
    activeTab === 'community' && isCommunitySpaceImageCarouselTab(activeCommunityTab)

  const carouselKey =
    activeTab === 'apartments'
      ? selectedApartment.id
      : `community-${selectedApartment.locationId}-${activeCommunityTab}`

  const carouselAriaLabel =
    activeTab === 'apartments'
      ? copy.carouselAriaLabel
      : activeCommunityTab === 'gallery'
        ? copy.community.carouselAriaLabel
        : activeCommunityTab === 'floorPlans'
          ? copy.community.floorPlansCarouselAriaLabel
          : copy.community.virtualTourPanelAriaLabel

  const carouselPagination =
    slides.length > 1 ? (
      <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2 md:bottom-10">
        <div className="pointer-events-auto flex items-center gap-3">
          <CarouselPrevious
            aria-label={copy.carouselPrevious}
            className={cn(CAROUSEL_ARROW_BUTTON_CLASS, 'top-auto left-auto size-10')}
            size="icon"
            variant="outline"
          />
          <CarouselDots
            getSlideLabel={(index) =>
              messages.common.goToSlide.replace('{n}', String(index + 1))
            }
          />
          <CarouselNext
            aria-label={copy.carouselNext}
            className={cn(CAROUSEL_ARROW_BUTTON_CLASS, 'top-auto right-auto size-10')}
            size="icon"
            variant="outline"
          />
        </div>
      </div>
    ) : null

  return (
    <section
      aria-labelledby="apartment-availability-heading"
      className="relative ml-[calc(50%-50vw)] w-screen max-w-[100vw] overflow-hidden bg-[#0E1112] md:ml-0 md:w-full md:max-w-none md:min-h-[55.75rem]"
    >
      <h2 className="sr-only" id="apartment-availability-heading">
        {copy.title}
      </h2>

      <Carousel
        key={carouselKey}
        aria-label={carouselAriaLabel}
        className="relative flex flex-col md:min-h-[55.75rem] bg-[#0E1112]"
        opts={{ align: 'start', loop: slides.length > 1 }}
        setApi={setCarouselApi}
      >
        <div
          className={cn(
            'relative z-50 order-1 w-full px-2 py-0',
            'md:pointer-events-none md:absolute md:inset-x-0 md:inset-y-0 md:z-50 md:flex md:items-center md:bg-transparent md:px-4',
          )}
        >
          <div className="flex w-full flex-col gap-1.5 overflow-visible py-1 md:pointer-events-auto md:max-w-[28rem] md:gap-0 md:space-y-2 md:rounded-none md:border-0 md:py-0">
            <SegmentedTabBar
              aria-label={copy.title}
              columnCount={showCommunityTab ? 2 : 1}
              layoutGroupId="apartment-availability-tabs"
              role="tablist"
            >
              <SegmentedTab
                aria-selected={activeTab === 'apartments'}
                indicatorLayoutId="apartment-availability-tab-indicator"
                isActive={activeTab === 'apartments'}
                onClick={() => setActiveTab('apartments')}
                role="tab"
              >
                <ApartmentsTabIcon />
                {copy.tabs.apartments}
              </SegmentedTab>
              {showCommunityTab ? (
                <SegmentedTab
                  aria-selected={activeTab === 'community'}
                  indicatorLayoutId="apartment-availability-tab-indicator"
                  isActive={activeTab === 'community'}
                  onClick={() => setActiveTab('community')}
                  role="tab"
                >
                  <CommunitySpacesTabIcon />
                  {copy.tabs.communitySpaces}
                </SegmentedTab>
              ) : null}
            </SegmentedTabBar>

            {activeTab === 'apartments' ? (
              <>
                {variant === 'landing' ? (
                  <ApartmentDropdown
                    apartments={apartments}
                    displayMode="location"
                    getPriceLabel={getPriceLabel}
                    locationOptions={locationDropdownOptions}
                    onSelect={handleLocationSelect}
                    selectedApartment={selectedApartment}
                    selectLabel={copy.locationSelectLabel}
                  />
                ) : null}

                {selectedRoomTypeGroup ? (
                  <RoomTypeDetailPanel
                    copy={{
                      apartmentTypeLabel: copy.apartmentTypeLabel,
                      nextRoomType: copy.nextRoomType,
                      previousRoomType: copy.previousRoomType,
                      rentEyebrowAfter: copy.rentEyebrowAfter,
                      rentEyebrowBefore: copy.rentEyebrowBefore,
                      rentEyebrowHighlight: copy.rentEyebrowHighlight,
                      rentTermLabel: copy.rentTermLabel,
                      roomTypePosition: copy.roomTypePosition,
                      startingAtLabel: copy.startingAtLabel,
                      variantTabsAriaLabel: copy.variantTabsAriaLabel,
                    }}
                    formatPrice={formatPrice}
                    layoutGroupId={`apartment-variant-tabs-${variant}`}
                    onNextRoomType={handleNextRoomType}
                    onPreviousRoomType={handlePreviousRoomType}
                    onVariantSelect={setSelectedApartmentId}
                    roomTypeCount={contextualRoomTypeGroups.length}
                    roomTypeIndex={Math.max(selectedRoomTypeIndex, 0)}
                    selectedApartment={selectedApartment}
                    selectedRoomTypeGroup={selectedRoomTypeGroup}
                  />
                ) : null}

                <p aria-live="polite" className="sr-only">
                  {selectionAnnouncement}
                </p>

                <div className="flex w-full gap-1.5 pt-1 md:gap-2 md:pt-2">
                  <Button
                    asChild
                    className={GLASS_ACTION_BUTTON_CLASS}
                    variant="outline"
                  >
                    <Link
                      aria-label={copy.viewDetailsAriaLabel.replace(
                        '{title}',
                        selectedApartment.name,
                      )}
                      href={roomDetailsHref}
                    >
                      {copy.viewDetails}
                    </Link>
                  </Button>
                  <Button
                    asChild
                    className={PRIMARY_ACTION_BUTTON_CLASS}
                    icon={
                      <img
                        alt=""
                        aria-hidden
                        className="size-5 shrink-0"
                        height={20}
                        loading="lazy"
                        src="/media/homepage/rent-arrow.svg"
                        width={20}
                      />
                    }
                    iconPosition="trailing"
                  >
                    <Link
                      href={rentLink.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {copy.rentApartments}
                      <span className="sr-only"> {messages.common.opensInNewTab}</span>
                    </Link>
                  </Button>
                </div>
              </>
            ) : (
              <CommunitySpacesPanel
                activeCommunityTab={activeCommunityTab}
                apartments={apartments}
                bookVisit={copy.bookVisit}
                bookVisitHref={getInspectionCalendarHref()}
                communityTabListAriaLabel={copy.community.tabListAriaLabel}
                communityTabs={copy.community.tabs}
                enabledTabs={enabledCommunityTabs}
                getPriceLabel={getPriceLabel}
                locationOptions={communityLocationOptions}
                locationSelectLabel={copy.locationSelectLabel}
                onCommunityTabChange={setActiveCommunityTab}
                onLocationSelect={handleLocationSelect}
                rentApartments={copy.rentApartments}
                rentApartmentsHref={localizePath(copy.rentApartmentsHref, locale)}
                selectedApartment={selectedApartment}
                showLocationDropdown={variant === 'landing'}
              />
            )}
          </div>
        </div>

        <div className="relative z-0 order-2 w-full shrink-0 overflow-hidden aspect-[422/512] md:absolute md:inset-0 md:order-none md:aspect-auto md:size-full">
          {activeTab === 'apartments' || showCommunityImageCarousel ? (
            <CarouselContent className="ml-0 h-full" viewportClassName="h-full">
              {slides.map((image, index) => (
                <CarouselItem key={`${carouselKey}-${index}`} className="relative h-full basis-full pl-0">
                  <Image
                    alt={image.alt}
                    className="object-cover object-center"
                    fill
                    quality={90}
                    sizes="100vw"
                    src={image.src}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          ) : null}

          {activeTab === 'community' && activeCommunityTab === 'virtualTour' && selectedCommunitySpaces?.virtualTour ? (
            <RoomGalleryVideoPanel
              className="size-full"
              isActive
              objectFit="cover"
              tour={{
                embedUrl: selectedCommunitySpaces.virtualTour.embedUrl,
                id: `community-tour-${selectedApartment.locationId}`,
                title: selectedCommunitySpaces.virtualTour.title,
              }}
            />
          ) : null}

          {activeTab === 'apartments' || showCommunityImageCarousel ? carouselPagination : null}
        </div>
      </Carousel>
    </section>
  )
}
